/**
 * Parse a range string like "1-3, 5, 7-10" into groups of 0-indexed page arrays.
 *
 * Rules:
 *  - Comma-separated tokens
 *  - Each token is either a single page number or a START-END range
 *  - START and END are 1-indexed (user-facing)
 *  - Pages may appear in multiple groups (overlaps are allowed)
 *
 * @param {string} input - Raw range string from user input
 * @param {number} totalPages - Total page count of the PDF (1-indexed max)
 * @returns {{ groups: number[][], error: string|null }}
 */
export function parseRanges(input, totalPages) {
  const trimmed = input.trim();
  if (!trimmed) return { groups: [], error: 'Enter at least one page range.' };

  const tokens = trimmed.split(',').map((t) => t.trim()).filter(Boolean);
  const groups = [];

  for (const token of tokens) {
    // Single page: "5"
    if (/^\d+$/.test(token)) {
      const n = parseInt(token, 10);
      if (n < 1) return { groups: [], error: `Page numbers start at 1. Got: "${token}".` };
      if (n > totalPages) {
        return { groups: [], error: `Page ${n} is out of range (document has ${totalPages} pages).` };
      }
      groups.push([n - 1]);
      continue;
    }

    // Range: "1-3"
    if (/^\d+-\d+$/.test(token)) {
      const [rawStart, rawEnd] = token.split('-').map(Number);
      if (rawStart < 1) return { groups: [], error: `Page numbers start at 1. Got: "${token}".` };
      if (rawStart > rawEnd) {
        return { groups: [], error: `Invalid range "${token}" — start must be ≤ end.` };
      }
      if (rawEnd > totalPages) {
        return {
          groups: [],
          error: `Page ${rawEnd} is out of range (document has ${totalPages} pages).`,
        };
      }
      groups.push(Array.from({ length: rawEnd - rawStart + 1 }, (_, i) => rawStart - 1 + i));
      continue;
    }

    return {
      groups: [],
      error: `Unrecognized token "${token}". Use format like: 1-3, 5, 7-10`,
    };
  }

  if (groups.length === 0) return { groups: [], error: 'Enter at least one page range.' };

  return { groups, error: null };
}

/**
 * Build page groups for "every page" mode: one group per page.
 *
 * @param {number} totalPages
 * @returns {number[][]}
 */
export function everyPageGroups(totalPages) {
  return Array.from({ length: totalPages }, (_, i) => [i]);
}

/**
 * Build page groups for "every N pages" mode.
 *
 * @param {number} totalPages
 * @param {number} n - Group size (must be >= 1)
 * @returns {number[][]}
 */
export function everyNGroups(totalPages, n) {
  const size = Math.max(1, Math.floor(n));
  const groups = [];
  for (let i = 0; i < totalPages; i += size) {
    groups.push(Array.from({ length: Math.min(size, totalPages - i) }, (_, j) => i + j));
  }
  return groups;
}

/**
 * Build a map of { pageIndex (0-based) → groupIndex } for color coding the thumbnail grid.
 * Only the first group assignment per page is recorded.
 *
 * @param {number[][]} groups
 * @returns {Record<number, number>}
 */
export function buildPageGroupMap(groups) {
  const map = {};
  groups.forEach((group, gIdx) => {
    group.forEach((pageIdx) => {
      if (map[pageIdx] === undefined) map[pageIdx] = gIdx;
    });
  });
  return map;
}
